/**
 * Two-layer SpacetimeDB providers.
 *
 * Outer (`SpacetimeProvider`) — always-on connections to the **org** module
 * (so we can list Sites) and the **launcher** module (so the topbar rocket
 * button has data). These don't depend on which Site the user is currently
 * looking at.
 *
 * Inner (`ProjectScopedSpacetime`) — connects to `{org}-{projectName}-core`
 * and `{org}-{projectName}-mp` for whichever Site is currently selected
 * (driven by `useTweaks().tweaks.siteIndex` + `useProjects()`). When the user
 * picks a different site the inner provider re-mounts with new builders; the
 * SDK's ref-counted ConnectionManager closes the old WebSockets cleanly.
 *
 * Per Samuel 2026-05-13: each site is a separate twin graph, so orientation
 * admin reads/writes must be scoped to the selected project — not the
 * hardcoded MineZero sandbox.
 *
 * Reconnect handling lives in `src/services/connectionManager/`. Every
 * builder resolves the JWT per connection attempt through AuthKit
 * (`withTokenProvider`), so the SDK's own reconnect loop and our harness
 * (silent disconnect watchdog, auto-reconnect with backoff, both via
 * `ConnectionManager.rebuild` from the public SDK) never replay a stale token.
 */

import { useCallback, useMemo, type ReactNode } from 'react';
import { SpacetimeDBMultiProvider, useSpacetimeDB } from 'spacetimedb/react';
import { DbConnection as CoreDb } from '@sdk/core';
import { DbConnection as MpDb } from '@sdk/multiplayer';
import { DbConnection as LauncherDb } from '@sdk/launcher';
import { noteConnectionState } from 'turbotwin-sdk/bug-report';
import { DbConnection as OrgDb } from '@sdk/org';
import { DbConnection as MinetwinDb } from '@sdk/minetwin';
import { DbConnection as AssetDb } from '@sdk/asset';
import { DbConnection as TelemetryDb } from '@sdk/telemetry';
import { useAuthKit } from './AuthProvider';
import { useOrgSession } from './OrgContext';
import { useProjects } from '../hooks/useProjects';
import { useTweaks } from '../hooks/useTweaks';
import { useTokenRef } from '@/services/connectionManager/useTokenRef';
import { useSilentDisconnectWatchdog } from '@/services/connectionManager/useSilentDisconnectWatchdog';
import { useAutoReconnect } from '@/services/connectionManager/useAutoReconnect';
import { useLauncherProbeDriver } from '@/hooks/useLauncherProbeDriver';
import { getPoolKey } from '@/services/connectionManager/getPoolKey';

const STDB_URI = import.meta.env.VITE_STDB_CLOUD_URI as string;
// Dev-only pins. Production DERIVES every org-scoped module name from the
// resolved org session (MULTI-ORG-PORTAL plan) — compiling an org in is the
// wrong-org-403s failure class the repo CLAUDE.md warns about. These are never
// set in the deploy workflow.
const ORG_MODULE_OVERRIDE = import.meta.env.VITE_ORG_MODULE_NAME as string | undefined;
const ORG_MP_MODULE_OVERRIDE = import.meta.env.VITE_ORG_MP_MODULE_NAME as string | undefined;
const MINETWIN_MODULE_OVERRIDE = import.meta.env.VITE_MINETWIN_MODULE_NAME as string | undefined;
// The stamp launcher DB (`{stamp}-launcher`) — per-STAMP, not per-org (org
// scoping happens inside it via the my_org_* views). The launcher EXE
// heartbeats into minetwin-launcher (told via the deep link's
// `launcher_module` param since the 2026-07-16 stamp cutover); watching the
// house `turbotwin-launcher` here meant `my_connected_clients` never showed
// the App row and the rocket could never go green (Stefan, 2026-07-29).
export const LAUNCHER_MODULE = import.meta.env.VITE_LAUNCHER_MODULE_NAME ?? 'minetwin-launcher';

export type OrgModules = {
  launcher: string;
  org: string;
  orgMp: string;
  minetwin: string;
  assets: string;
};

/** Org-scoped module names for a resolved org slug (e.g. `minetwin-default`). */
export function orgModulesFor(slug: string): OrgModules {
  return {
    launcher: LAUNCHER_MODULE,
    org: ORG_MODULE_OVERRIDE || `${slug}-org`,
    orgMp: ORG_MP_MODULE_OVERRIDE || `${slug}-mp`,
    minetwin: MINETWIN_MODULE_OVERRIDE || `${slug}-srv-console`,
    assets: `${slug}-assets`,
  };
}

export function moduleNameFor(
  slug: string,
  projectName: string,
  kind: 'core' | 'mp' | 'telemetry',
): string {
  return `${slug}-${projectName}-${kind}`;
}

/** Per-attempt token provider (see `useTokenRef`). Rejects when AuthKit has
 *  no live session, which fails the attempt locally instead of replaying a
 *  dead JWT at the server. */
type GetToken = () => Promise<string>;

/**
 * Report a module socket's state to the bug-report session log, so a report's
 * system info carries the per-module connection summary (same line the
 * platform webclient writes). The builder keeps a list per event, so these
 * callbacks sit next to the module's own.
 */
function noted<
  B extends {
    onConnect(cb: () => void): B;
    onConnectError(cb: () => void): B;
    onDisconnect(cb: () => void): B;
  },
>(builder: B, dbName: string): B {
  return builder
    .onConnect(() => noteConnectionState(dbName, 'connected'))
    .onConnectError(() => noteConnectionState(dbName, 'error'))
    .onDisconnect(() => noteConnectionState(dbName, 'disconnected'));
}

// Builder factories. `withTokenProvider` is asked for the JWT on EVERY
// connection attempt, including the SDK ConnectionManager's own reconnect
// loop, which reuses the same builder object after a drop. With
// `withToken(string)` that loop replayed the token frozen at build time:
// once it expired (WorkOS access tokens are short-lived) every attempt was a
// guaranteed 401 and the portal sat empty until a page reload (#174).

function buildLauncherBuilder(getToken: GetToken) {
  return LauncherDb.builder()
    .withUri(STDB_URI)
    .withDatabaseName(LAUNCHER_MODULE)
    .withTokenProvider(getToken)
    .onConnect((conn) => {
      conn
        .subscriptionBuilder()
        .onApplied(() => console.info('[stdb:launcher] subscriptions applied'))
        .onError((ctx) =>
          console.error('[stdb:launcher] subscription error', ctx),
        )
        .subscribe([
          // Raw `app` / `app_version` / `channel_version` tables are
          // private server-side on the deployed turbotwin-launcher; only
          // the org-scoped `my_org_*` views are subscribable for non-
          // launcher-admins. A single missing/private table in the batch
          // fails the ENTIRE subscription, leaves the connection inactive
          // (red rocket), and reads as "launcher offline" in the topbar.
          // Per the TurboTwin webclient's LauncherDBContext.tsx — they
          // hit the same outage globally and switched to the views.
          'SELECT * FROM my_org_apps',
          'SELECT * FROM my_org_app_versions',
          // Per-channel pinned-version pointer. Needed to resolve the build
          // a channel points at — the pin can reference a version published
          // under a different channel (e.g. Stable pinned to a Dev build),
          // which filtering app_version by published channel alone misses.
          'SELECT * FROM my_org_channel_versions',
          'SELECT * FROM my_app_status',
          'SELECT * FROM my_commands',
          'SELECT * FROM my_connected_clients',
          'SELECT * FROM my_download_progress',
          // #193: whether the version the channel resolves to is already on
          // THIS machine, so the button can say Download / Update / Launch
          // before the click instead of discovering it from a 202.
          'SELECT * FROM my_installed_versions',
          // Drives the auto-allowlist hook — when the portal mounts on a
          // hostname the launcher EXE hasn't seen before, the hook reads
          // this view and calls `add_cors_origin` once so the local
          // launcher's HTTP API will accept `Origin: https://<host>`
          // preflights.
          'SELECT * FROM my_org_cors_origin',
        ]);
      // Admin per-channel launch args (e.g. -TurboTwinProject=…), needed so a
      // portal launch hands the Unreal app the same context the webclient
      // does. Deliberately a SEPARATE subscription: a failed batch fails
      // WHOLE, and this raw public table is not part of the my_org_* view set
      // the rest of the launcher UI depends on — isolating it means a schema
      // drift here can't blind the launcher panel.
      conn
        .subscriptionBuilder()
        .onError((ctx) =>
          console.warn('[stdb:launcher] channel-config subscription error', ctx),
        )
        .subscribe(['SELECT * FROM app_channel_config']);
    })
    .onConnectError((_ctx, err) =>
      console.error('[stdb:launcher] connect error', err),
    )
    .onDisconnect((_ctx, err) =>
      console.warn('[stdb:launcher] disconnected', err),
    );
}

function buildOrgBuilder(getToken: GetToken, dbName: string) {
  return OrgDb.builder()
    .withUri(STDB_URI)
    .withDatabaseName(dbName)
    .withTokenProvider(getToken)
    .onConnect((conn) => {
      conn
        .subscriptionBuilder()
        .onApplied(() => console.info('[stdb:org] subscriptions applied'))
        .onError((ctx) => console.error('[stdb:org] subscription error', ctx))
        .subscribe([
          // `project` / `project_user` went fully private (UC1 Phase A);
          // clients read them through the identity-scoped `my_projects` /
          // `my_project_users` views (org admins use the
          // `admin_list_projects` procedure for the full list).
          'SELECT * FROM my_projects',
          'SELECT * FROM my_project_users',
          'SELECT * FROM admin_user',
          // module_deployment is the authoritative source for per-project
          // SpacetimeDB database names (see TurboTwin SDK docs:
          // typescript/org-module.md#Getting Project Database Names). We
          // use it to (a) hide projects with no Active Core deployment from
          // the topbar dropdown and (b) source `core.databaseName` from the
          // row instead of string-constructing `{org}-{project}-core`.
          // Picking samuel-sandbox without this subscription tried to
          // connect to a non-existent module and produced a connect-error
          // loop reported by Cloud Lead 2026-06-22.
          'SELECT * FROM module_deployment',
        ]);
      // #219: the service registry says which project each onboarding agent
      // was registered for, so Site Settings can refuse another site's
      // agent. Deliberately a SEPARATE subscription (same reason as the
      // launcher's channel config): a failed batch fails whole, and the
      // topbar's project list must never go dark over this.
      conn
        .subscriptionBuilder()
        .onError((ctx) =>
          console.warn('[stdb:org] registered_service subscription error', ctx),
        )
        .subscribe(['SELECT * FROM registered_service']);
    })
    .onConnectError((_ctx, err) =>
      console.error('[stdb:org] connect error', err),
    )
    .onDisconnect((_ctx, err) =>
      console.warn('[stdb:org] disconnected', err),
    );
}

function buildMinetwinBuilder(getToken: GetToken, dbName: string) {
  return MinetwinDb.builder()
    .withUri(STDB_URI)
    .withDatabaseName(dbName)
    .withTokenProvider(getToken)
    .onConnect((conn) => {
      conn
        .subscriptionBuilder()
        .onApplied(() => console.info('[stdb:minetwin] subscriptions applied'))
        .onError((ctx) =>
          console.error('[stdb:minetwin] subscription error', ctx),
        )
        .subscribe([
          'SELECT * FROM my_user',
          // Admin-facing views (visible_*): scoped via admin_project_ids
          // server-side (OrgAdmin/OrgViewer see every project; Admin
          // sees attached sites; Member/Contractor get an empty
          // workforce roster — their own data is in my_*).
          //
          // Post-Phase-3 (Workforce Alignment): visible_employees +
          // visible_employee_site_memberships collapsed into
          // visible_users + visible_user_sites; the new
          // visible_pending_users surfaces coordinator-added rows
          // before their first sign-in.
          'SELECT * FROM visible_users',
          'SELECT * FROM visible_pending_users',
          'SELECT * FROM visible_user_sites',
          'SELECT * FROM visible_project_agents',
          'SELECT * FROM visible_orientations',
          // assignable_orientations subscription DELIBERATELY OMITTED
          // for now — the view ships in the bindings (Samuel ed2d4b7)
          // but the destructive republish that creates it server-side
          // hasn't run yet. Subscribing here when the view doesn't
          // exist breaks the whole subscription batch and empties
          // Orientation Assignment. Re-enable after the next republish; until
          // then CohortBuilder uses useOrientations with a client-side
          // status === Active filter.
          'SELECT * FROM visible_orientation_steps',
          'SELECT * FROM visible_orientation_step_links',
          'SELECT * FROM visible_orientation_step_scripts',
          'SELECT * FROM visible_script_actions',
          'SELECT * FROM cohort_progress',
          'SELECT * FROM cohort_assignment_progress',
          'SELECT * FROM cohort_step_metrics',
          // Trainee-facing views (my_*): the signed-in user's own
          // record (now a unified User) + assignments + per-step state.
          // Empty for users without a workforce binding.
          'SELECT * FROM my_user_sites',
          'SELECT * FROM my_assignments',
          'SELECT * FROM my_assignment_progress',
          'SELECT * FROM my_assignment_attempts',
          'SELECT * FROM my_assignment_step_metrics',
        ]);
      // Durable bell notifications — DELIBERATELY a separate subscription
      // (the launcher's app_channel_config pattern): a batch fails as a
      // WHOLE if one view is missing server-side, and my_notifications only
      // exists once the module republish carrying it has landed on this
      // org's console. Isolated, a lagging org just has an empty bell
      // instead of a dead portal.
      conn
        .subscriptionBuilder()
        .onError((ctx) =>
          console.warn(
            '[stdb:minetwin] notifications subscription error (module republish not landed on this org yet?)',
            ctx,
          ),
        )
        .subscribe(['SELECT * FROM my_notifications']);
      // Comprehension checks: the questions and their answer keys for the
      // Orientation Editor, the cohort's answer rows for the Review Queue.
      // Separate batch for the same reason as the bell above: these views only
      // exist once the checks republish has landed on this org, and a missing
      // view fails its whole batch. Isolated, a lagging org just authors
      // without checks. visible_orientation_check_keys is ADMIN-scoped by
      // design (a Member must never read an answer key), so a non-admin
      // session legitimately gets rows here with no key.
      conn
        .subscriptionBuilder()
        .onError((ctx) =>
          console.warn(
            '[stdb:minetwin] checks subscription error (module republish not landed on this org yet?)',
            ctx,
          ),
        )
        .subscribe([
          'SELECT * FROM visible_orientation_checks',
          'SELECT * FROM visible_orientation_check_keys',
          'SELECT * FROM cohort_check_answers',
        ]);
      // Cohort + group entity views (cohort plan Phase 4, group plan Phase 5)
      // — the rosters behind the Cohorts tab and the group runs layered on
      // its pairings. One batch: both view families landed in the same
      // 2026-09-15 schema, so their availability is identical. Isolated for
      // the same reason as the bell and the checks: a lagging org gets an
      // empty Cohorts tab instead of a dead portal.
      conn
        .subscriptionBuilder()
        .onError((ctx) =>
          console.warn(
            '[stdb:minetwin] cohort/group subscription error (module republish not landed on this org yet?)',
            ctx,
          ),
        )
        .subscribe([
          'SELECT * FROM visible_cohorts',
          'SELECT * FROM visible_cohort_members',
          'SELECT * FROM visible_cohort_assignments',
          'SELECT * FROM visible_group_runs',
          'SELECT * FROM visible_group_run_participants',
          // Trainee-scoped group views (member-or-leader gated): the trainee
          // portal's lobby text reads these. Same publish, same batch.
          'SELECT * FROM my_group_run',
          'SELECT * FROM my_group_run_participants',
        ]);
      // Site rights (ORIENTATION-ROLES M7, M8): the caller's own rights for
      // the page gates, the admin scope's rights for the Allow authoring
      // switch, and the admin-scope site list. Own batch, same reason as
      // above: an org whose module lacks these views keeps the rest.
      conn
        .subscriptionBuilder()
        .onError((ctx) =>
          console.warn(
            '[stdb:minetwin] site rights subscription error (module republish not landed on this org yet?)',
            ctx,
          ),
        )
        .subscribe([
          'SELECT * FROM my_site_rights',
          'SELECT * FROM visible_site_rights',
          'SELECT * FROM admin_site_ids',
        ]);
    })
    .onConnectError((_ctx, err) =>
      console.error('[stdb:minetwin] connect error', err),
    )
    .onDisconnect((_ctx, err) =>
      console.warn('[stdb:minetwin] disconnected', err),
    );
}

function buildMpBuilder(
  getToken: GetToken,
  presenceDisplayName: string,
  dbName: string,
) {
  return MpDb.builder()
    .withUri(STDB_URI)
    .withDatabaseName(dbName)
    .withTokenProvider(getToken)
    .onConnect((conn) => {
      conn
        .subscriptionBuilder()
        .onApplied(() => {
          console.info('[stdb:mp] subscriptions applied');
          try {
            conn.reducers.registerPresence({
              displayName: presenceDisplayName,
              avatarColor: undefined,
              clientType: { tag: 'Webclient' },
              clientVersion: '0.1.0',
              actorType: { tag: 'Player' },
              agentType: undefined,
              associatedTwinId: undefined,
            });
            console.info('[stdb:mp] register_presence sent');
          } catch (err) {
            console.error('[stdb:mp] register_presence failed', err);
          }
        })
        .onError((ctx) => console.error('[stdb:mp] subscription error', ctx))
        .subscribe([
          'SELECT * FROM presence_state',
          'SELECT * FROM discoverable_sessions',
        ]);
    })
    .onConnectError((_ctx, err) =>
      console.error('[stdb:mp] connect error', err),
    )
    .onDisconnect((_ctx, err) =>
      console.warn('[stdb:mp] disconnected', err),
    );
}

// Project-scoped `{slug}-{project}-telemetry` connection. Only `twin_transform`
// (the live per-twin pose stream) is subscribed — it feeds the 2D map's moving
// truck markers, the same table the webclient map and the 3D viewer animate
// from. Project-scoped like `core` (registered in ProjectScopedSpacetime).
function buildTelemetryBuilder(getToken: GetToken, dbName: string) {
  return TelemetryDb.builder()
    .withUri(STDB_URI)
    .withDatabaseName(dbName)
    .withTokenProvider(getToken)
    .onConnect((conn) => {
      conn
        .subscriptionBuilder()
        .onApplied(() => console.info('[stdb:telemetry] subscriptions applied'))
        .onError((ctx) => console.error('[stdb:telemetry] subscription error', ctx))
        .subscribe(['SELECT * FROM twin_transform']);
    })
    .onConnectError((_ctx, err) =>
      console.error('[stdb:telemetry] connect error', err),
    )
    .onDisconnect((_ctx, err) =>
      console.warn('[stdb:telemetry] disconnected', err),
    );
}

function buildAssetsBuilder(getToken: GetToken, dbName: string) {
  return AssetDb.builder()
    .withUri(STDB_URI)
    .withDatabaseName(dbName)
    .withTokenProvider(getToken)
    .onConnect((conn) => {
      conn
        .subscriptionBuilder()
        .onApplied(() => console.info('[stdb:assets] subscriptions applied'))
        .onError((ctx) => console.error('[stdb:assets] subscription error', ctx))
        // Only the asset rows (for `thumbnail_url`) — joined with core's
        // model_asset_binding to put model thumbnails on twin map markers.
        .subscribe(['SELECT * FROM my_asset']);
    })
    .onConnectError((_ctx, err) =>
      console.error('[stdb:assets] connect error', err),
    )
    .onDisconnect((_ctx, err) =>
      console.warn('[stdb:assets] disconnected', err),
    );
}

function buildCoreBuilder(
  getToken: GetToken,
  coreModule: string,
  hasProject: boolean,
  label: string,
) {
  return CoreDb.builder()
    .withUri(STDB_URI)
    .withDatabaseName(coreModule)
    .withTokenProvider(getToken)
    .onConnect((conn) => {
      if (!hasProject) return;
      conn
        .subscriptionBuilder()
        .onApplied(() =>
          console.info(`[stdb:core:${label}] subscriptions applied`),
        )
        .onError((ctx) =>
          console.error(`[stdb:core:${label}] subscription error`, ctx),
        )
        .subscribe([
          'SELECT * FROM digital_twin',
          // Per-twin geo location (orientation editor's 2D twin markers).
          // Public table — the TurboTwin webclient subscribes it the same way.
          'SELECT * FROM turbotwin_metadata',
          'SELECT * FROM twin_relationship_instance',
          // Geospatial twin properties (road centerlines, areas, points) —
          // drawn as overlays on the orientation editor's 2D map, same as the
          // webclient map page.
          'SELECT * FROM complex_property',
          'SELECT * FROM primitive_property_string',
          'SELECT * FROM twin_property',
          'SELECT * FROM dtdl_interface',
        ]);
      // dtmi → asset id, for model thumbnails on map markers. DELIBERATELY a
      // separate batch: a failed batch fails WHOLE, and an org core deployed
      // before this table exists would otherwise blind us to every core table.
      conn
        .subscriptionBuilder()
        .onError((ctx) =>
          console.warn(`[stdb:core:${label}] model_asset_binding unavailable (older core?)`, ctx),
        )
        .subscribe(['SELECT * FROM model_asset_binding']);
      // Project config keys — the twin display-name chain reads
      // "display_name_property" (TWIN-DISPLAY-NAME). Separate batch for the
      // same fail-whole reason as above.
      conn
        .subscriptionBuilder()
        .onError((ctx) =>
          console.warn(`[stdb:core:${label}] system_config unavailable (older core?)`, ctx),
        )
        .subscribe(['SELECT * FROM system_config']);
    })
    .onConnectError((_ctx, err) => {
      // The no-site sentinel connection is EXPECTED to fail (the database
      // doesn't exist) — logging it as an error reads like an outage.
      if (!hasProject) return;
      console.error(`[stdb:core:${label}] connect error`, err);
    })
    .onDisconnect((_ctx, err) => {
      if (!hasProject) return;
      console.warn(`[stdb:core:${label}] disconnected`, err);
    });
}

export function SpacetimeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthKit();
  // Total by construction: OrgProvider gates this whole subtree on a resolved
  // org, so module names derive unconditionally. An org switch changes the
  // slug → the `key` below remounts every provider with fresh connections.
  const { org } = useOrgSession();
  const modules = useMemo(() => orgModulesFor(org.slug), [org.slug]);
  const { getToken, refreshToken, tokenReady } = useTokenRef();

  // Stable presence-display name for MP, captured into the builder
  // factory so reconnects don't churn the rendered name.
  const presenceDisplayName = useMemo(
    () =>
      [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
      user?.email ||
      'Admin',
    [user?.firstName, user?.lastName, user?.email],
  );

  // Factories are stable wrt user identity; they read the token lazily
  // each time they're called (initial build + every rebuild).
  const launcherFactory = useCallback(
    () => noted(buildLauncherBuilder(getToken), modules.launcher),
    [getToken, modules.launcher],
  );
  const orgFactory = useCallback(
    () => noted(buildOrgBuilder(getToken, modules.org), modules.org),
    [getToken, modules.org],
  );
  const minetwinFactory = useCallback(
    () => noted(buildMinetwinBuilder(getToken, modules.minetwin), modules.minetwin),
    [getToken, modules.minetwin],
  );
  const mpFactory = useCallback(
    () => noted(buildMpBuilder(getToken, presenceDisplayName, modules.orgMp), modules.orgMp),
    [getToken, presenceDisplayName, modules.orgMp],
  );
  const assetsFactory = useCallback(
    () => noted(buildAssetsBuilder(getToken, modules.assets), modules.assets),
    [getToken, modules.assets],
  );

  const outerConnections = useMemo(() => {
    if (!user) return null;
    // Defer builder creation until the first token has actually
    // resolved into `tokenRef.current`. Without this gate, the first
    // render runs the factories with an empty token, the SDK opens
    // WS connections without `?token=…`, and the server closes them
    // immediately with code 1006 (reported 2026-06-27). Subsequent
    if (!tokenReady) return null;
    return {
      launcher: launcherFactory(),
      org: orgFactory(),
      minetwin: minetwinFactory(),
      mp: mpFactory(),
      assets: assetsFactory(),
    };
  }, [user, tokenReady, launcherFactory, orgFactory, minetwinFactory, mpFactory, assetsFactory]);

  if (!outerConnections || !user) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--ink, #060E18)',
          color: 'var(--muted, #7A8499)',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        Connecting…
      </div>
    );
  }

  return (
    // Keyed on the org slug: an org switch tears down every outer connection
    // and pool key cleanly instead of mutating them in place.
    <SpacetimeDBMultiProvider key={org.slug} connections={outerConnections}>
      <OuterReconnectHarness
        modules={modules}
        refreshToken={refreshToken}
        launcherFactory={launcherFactory}
        orgFactory={orgFactory}
        minetwinFactory={minetwinFactory}
        mpFactory={mpFactory}
        assetsFactory={assetsFactory}
      />
      <ProjectScopedSpacetime
        getToken={getToken}
        refreshToken={refreshToken}
      >
        {children}
      </ProjectScopedSpacetime>
    </SpacetimeDBMultiProvider>
  );
}

/**
 * Per-module reconnect wiring for the outer providers. Lives as a child
 * of the MultiProvider so `useSpacetimeDB(key)` can read the live state
 * for each connection. The harness renders nothing.
 */
function OuterReconnectHarness({
  modules,
  refreshToken,
  launcherFactory,
  orgFactory,
  minetwinFactory,
  mpFactory,
  assetsFactory,
}: {
  modules: OrgModules;
  refreshToken: () => Promise<string | null>;
  launcherFactory: () => ReturnType<typeof buildLauncherBuilder>;
  orgFactory: () => ReturnType<typeof buildOrgBuilder>;
  minetwinFactory: () => ReturnType<typeof buildMinetwinBuilder>;
  mpFactory: () => ReturnType<typeof buildMpBuilder>;
  assetsFactory: () => ReturnType<typeof buildAssetsBuilder>;
}) {
  const launcherState = useSpacetimeDB('launcher');
  const orgState = useSpacetimeDB('org');
  const minetwinState = useSpacetimeDB('minetwin');
  const mpState = useSpacetimeDB('mp');
  const assetsState = useSpacetimeDB('assets');

  // The provider tree is keyed on the org slug, so this harness remounts on
  // an org switch and these once-per-mount keys stay correct.
  const launcherKey = useMemo(() => getPoolKey(STDB_URI, modules.launcher), [modules.launcher]);
  const orgKey = useMemo(() => getPoolKey(STDB_URI, modules.org), [modules.org]);
  const minetwinKey = useMemo(() => getPoolKey(STDB_URI, modules.minetwin), [modules.minetwin]);
  const mpKey = useMemo(() => getPoolKey(STDB_URI, modules.orgMp), [modules.orgMp]);
  const assetsKey = useMemo(() => getPoolKey(STDB_URI, modules.assets), [modules.assets]);


  useSilentDisconnectWatchdog(launcherKey, launcherState, launcherFactory, 'launcher', { beforeRebuild: refreshToken });
  useSilentDisconnectWatchdog(orgKey, orgState, orgFactory, 'org', { beforeRebuild: refreshToken });
  useSilentDisconnectWatchdog(minetwinKey, minetwinState, minetwinFactory, 'minetwin', { beforeRebuild: refreshToken });
  useSilentDisconnectWatchdog(mpKey, mpState, mpFactory, 'mp', { beforeRebuild: refreshToken });
  useSilentDisconnectWatchdog(assetsKey, assetsState, assetsFactory, 'assets', { beforeRebuild: refreshToken });

  useAutoReconnect(launcherKey, launcherState, launcherFactory, { beforeReconnect: refreshToken });
  useAutoReconnect(orgKey, orgState, orgFactory, { beforeReconnect: refreshToken });
  useAutoReconnect(minetwinKey, minetwinState, minetwinFactory, { beforeReconnect: refreshToken });
  useAutoReconnect(mpKey, mpState, mpFactory, { beforeReconnect: refreshToken });
  useAutoReconnect(assetsKey, assetsState, assetsFactory, { beforeReconnect: refreshToken });

  // Local-API tie-breaker for the launcher: while the STDB cache says
  // "no launcher connected", poll 127.0.0.1:19871/status to catch (a) a
  // stale subscription on a half-open socket (rebuilds it) and (b) the
  // launcher being signed in as a different user than this tab.
  useLauncherProbeDriver(launcherKey, launcherFactory);

  return null;
}

function ProjectScopedSpacetime({
  getToken,
  refreshToken,
  children,
}: {
  getToken: GetToken;
  refreshToken: () => Promise<string | null>;
  children: ReactNode;
}) {
  const { sites } = useProjects();
  const { tweaks } = useTweaks();
  const { org } = useOrgSession();
  const orgSlugForProjects = org.slug;

  // Resolve the selected Site to a real project. If the org subscription
  // hasn't delivered any projects yet, we have no live Site → skip the
  // core connection entirely. The OLD silent fallback to
  // `minetwin-ai-minezero-sandbox-core` was the bug that made the topbar
  // picker disagree with the data: it would happily show twins from
  // MineZero-Sandbox while the picker said the user had selected an empty
  // project of their own.
  const selectedProject = sites[tweaks.siteIndex] ?? sites[0];
  const projectName = selectedProject?.projectName;
  // When no real project is selected we still need to register a `core`
  // connection — `useSpacetimeDB('core')` is a hook that can't be skipped
  // and would throw if the key were missing. Use an obviously-fake module
  // name so the connection fails silently; useTable returns empty arrays
  // and the OrientationAdmin diagnostic surfaces `core (offline)`.
  const NO_SITE_SENTINEL = '__no-site-selected__';
  // String-construction by the `{org}-{project}-core` naming convention.
  // Per TurboTwin SDK docs this should source from `org.module_deployment`,
  // but the live table is sparsely populated today — see useProjects.ts
  // comment and the tracking issue. Until Samuel backfills deployment rows
  // for every deployed Core, we trust the naming convention.
  const coreModule = projectName
    ? moduleNameFor(orgSlugForProjects, projectName, 'core')
    : NO_SITE_SENTINEL;
  // Telemetry rides the same `{slug}-{project}-telemetry` naming convention as
  // core (see TelemetryDBContext in the webclient). The no-site sentinel MUST
  // differ from core's: the SDK pools connections by (uri, moduleName), so a
  // shared sentinel makes `core` and `telemetry` resolve to ONE pooled
  // connection (the CoreDb built first) — then `useTable('telemetry', …)` reads
  // a db with no twin_transform table and crashes on `.iter()` of undefined.
  // A distinct sentinel keeps telemetry its own (silently-failing) TelemetryDb.
  const telemetryModule = projectName
    ? moduleNameFor(orgSlugForProjects, projectName, 'telemetry')
    : `${NO_SITE_SENTINEL}-telemetry`;
  const hasProject = Boolean(projectName);
  const label = hasProject && projectName ? projectName : 'no-site';

  const coreFactory = useCallback(
    () => {
      const builder = buildCoreBuilder(getToken, coreModule, hasProject, label);
      // The no-site sentinel connection is expected to fail; keep it out of
      // the connection summary.
      return hasProject ? noted(builder, coreModule) : builder;
    },
    [getToken, coreModule, hasProject, label],
  );
  const telemetryFactory = useCallback(
    () => {
      const builder = buildTelemetryBuilder(getToken, telemetryModule);
      return hasProject ? noted(builder, telemetryModule) : builder;
    },
    [getToken, telemetryModule, hasProject],
  );

  const innerConnections = useMemo(
    () => ({ core: coreFactory(), telemetry: telemetryFactory() }),
    [coreFactory, telemetryFactory],
  );

  // `key` forces a fresh mount when the project changes, so the inner
  // ConnectionManager pool releases the previous core/mp connections cleanly.
  return (
    <SpacetimeDBMultiProvider key={projectName ?? '__nosite__'} connections={innerConnections}>
      {/* No harness for the no-site sentinel: watchdog/auto-reconnect would
          keep re-dialing the nonexistent placeholder database, spamming the
          console with doomed WS attempts. One initial failed attempt is
          enough — the provider remounts with a real harness the moment a
          project resolves (keyed on projectName). */}
      {hasProject && (
        <InnerReconnectHarness
          coreModule={coreModule}
          telemetryModule={telemetryModule}
          refreshToken={refreshToken}
          coreFactory={coreFactory}
          telemetryFactory={telemetryFactory}
        />
      )}
      {children}
    </SpacetimeDBMultiProvider>
  );
}

function InnerReconnectHarness({
  coreModule,
  telemetryModule,
  refreshToken,
  coreFactory,
  telemetryFactory,
}: {
  coreModule: string;
  telemetryModule: string;
  refreshToken: () => Promise<string | null>;
  coreFactory: () => ReturnType<typeof buildCoreBuilder>;
  telemetryFactory: () => ReturnType<typeof buildTelemetryBuilder>;
}) {
  const coreState = useSpacetimeDB('core');
  const coreKey = useMemo(() => getPoolKey(STDB_URI, coreModule), [coreModule]);

  useSilentDisconnectWatchdog(coreKey, coreState, coreFactory, `core:${coreModule}`, {
    beforeRebuild: refreshToken,
  });
  useAutoReconnect(coreKey, coreState, coreFactory, {
    beforeReconnect: refreshToken,
  });

  const telemetryState = useSpacetimeDB('telemetry');
  const telemetryKey = useMemo(() => getPoolKey(STDB_URI, telemetryModule), [telemetryModule]);

  useSilentDisconnectWatchdog(telemetryKey, telemetryState, telemetryFactory, `telemetry:${telemetryModule}`, {
    beforeRebuild: refreshToken,
  });
  useAutoReconnect(telemetryKey, telemetryState, telemetryFactory, {
    beforeReconnect: refreshToken,
  });

  return null;
}
